import React, { useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { MAX_GUIDE_CHARS, validateGuideBody } from 'shared/lib/aiGuide/limits';
import Button from '~/app/ui/Button';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import EmptyState from '~/app/ui/EmptyState';
import { S, A, TONES } from '~/app/ui/tokens';
import GuideDocument from './GuideDocument';
import useGuideStyles from './styles';

type Guide = {
  version: number;
  body: string;
  notes?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  builtIn: boolean;
  builtInUpdated: boolean;
};

const FIELDS = 'version body notes createdBy createdAt builtIn builtInUpdated';
const AI_GUIDE = gql`query AiGuide { aiGuide { ${FIELDS} } }`;
const AI_GUIDE_VERSIONS = gql`query AiGuideVersions { aiGuideVersions { ${FIELDS} } }`;
const PREVIEW = gql`query AiGuidePreview($body: String) { aiGuideText(body: $body) }`;
const SAVE = gql`mutation SaveAiGuide($body: String!, $notes: String, $baseVersion: Int!, $fromBuiltIn: Boolean) {
  saveAiGuide(body: $body, notes: $notes, baseVersion: $baseVersion, fromBuiltIn: $fromBuiltIn) { ${FIELDS} }
}`;

const useStyles = createUseStyles({
  root: { display: 'grid', gap: 16, color: S.fg },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(260px, 1fr)', gap: 24, '@media (max-width: 960px)': { gridTemplateColumns: 'minmax(0, 1fr)' } },
  column: { display: 'grid', gap: 10, alignContent: 'start' },
  label: { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: S.muted },
  meta: { fontSize: 13, color: S.muted },
  editor: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 480,
    padding: '12px 14px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    background: S.field,
    color: S.fg,
    fontFamily: S.mono,
    fontSize: 12.5,
    lineHeight: 1.55,
    resize: 'vertical',
    '&:focus': { outline: 'none', borderColor: S.fg, boxShadow: `0 0 0 1px ${S.fg}` },
    '&[readonly]': { opacity: 0.7 }
  },
  input: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 38,
    padding: '8px 12px',
    fontSize: 13,
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    background: S.field,
    color: S.fg,
    fontFamily: 'inherit',
    '&:focus': { outline: 'none', borderColor: S.fg, boxShadow: `0 0 0 1px ${S.fg}` }
  },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  notice: { padding: '10px 12px', borderRadius: A.radius, background: TONES.amber.bg, color: TONES.amber.fg, fontSize: 13, lineHeight: 1.5 },
  versions: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 },
  version: { display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr) auto', gap: 10, alignItems: 'center', padding: '8px 10px', border: `1px solid ${S.line}`, borderRadius: A.radius, background: S.chrome, fontSize: 13 },
  over: { color: S.red, fontWeight: 600 }
});

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '');
const normalize = (text: string) => text.replace(/\r\n?/g, '\n');

type Blocked = { location: { pathname: string; search?: string; hash?: string }; action: string };

/**
 * Super admins edit the part of the guide people write. Saving makes a new
 * version; nothing is ever overwritten, so any earlier version can be loaded
 * back. The rules, screens, access, categories, templates and section code
 * rules around it are generated and update by themselves.
 */
const AiGuideEditor: React.FC = () => {
  const c = useStyles();
  const g = useGuideStyles();
  const history = useHistory();
  const { data, loading, error, refetch } = useQuery<{ aiGuide: Guide }>(AI_GUIDE, { fetchPolicy: 'network-only' });
  const { data: versionsData, error: versionsError, refetch: refetchVersions } = useQuery<{ aiGuideVersions: Guide[] }>(AI_GUIDE_VERSIONS, { fetchPolicy: 'network-only' });
  const [save, { loading: saving }] = useMutation(SAVE, { refetchQueries: ['AiGuide', 'AiGuideVersions', 'AiGuideText'], awaitRefetchQueries: true });
  // Not cached: each preview is a different (possibly long) draft.
  const [runPreview, { data: previewData, loading: previewing, error: previewError }] = useLazyQuery<{ aiGuideText: string }>(PREVIEW, { fetchPolicy: 'no-cache' });
  const current = data?.aiGuide || null;
  const versions = versionsData?.aiGuideVersions || [];
  const builtIn = versions.find(v => v.builtIn) || null;
  const [draft, setDraft] = useState<string | null>(null);
  // The version in use when this draft started, and whether its text came from the built-in guide.
  const [base, setBase] = useState<number>(0);
  const [fromBuiltIn, setFromBuiltIn] = useState(false);
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Closed until someone means to edit: the text box is what AI assistants follow, so it shouldn't sit open.
  const [editing, setEditing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const blockedRef = useRef<Blocked | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (current && draft === null) {
      setDraft(current.body);
      setBase(current.version);
      setFromBuiltIn(current.builtIn);
    }
  }, [current, draft]);

  // Only an open editor holds unsaved work (a closed one starts again from the version in use).
  const changed = editing && !!current && draft !== null && normalize(draft) !== current.body;

  useEffect(() => {
    if (editing && bodyRef.current) bodyRef.current.focus({ preventScroll: true });
  }, [editing]);

  // Unsaved edits: ask before leaving, in the app (Genesis's own dialog) and when closing or reloading the tab.
  useEffect(() => {
    if (!changed) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    const unblock = history.block((location, action) => {
      blockedRef.current = { location, action };
      setConfirmLeave(true);
      return false;
    });
    unblockRef.current = unblock;
    return () => {
      window.removeEventListener('beforeunload', warn);
      unblock();
      unblockRef.current = null;
    };
  }, [changed, history]);

  const leaveAnyway = () => {
    if (unblockRef.current) unblockRef.current();
    unblockRef.current = null;
    const blocked = blockedRef.current;
    blockedRef.current = null;
    setConfirmLeave(false);
    if (!blocked) return;
    if (blocked.action === 'REPLACE') history.replace(blocked.location);
    else history.push(blocked.location);
  };

  if (error && !current) {
    return <EmptyState tone="error" title="Couldn’t load the guide" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState>;
  }
  if (loading || !current || draft === null) return <div className={c.meta}>Loading the guide…</div>;

  const problems = validateGuideBody(draft);
  const edit = (body: string, startedFrom: { builtIn: boolean } | null, note?: string) => {
    setDraft(body);
    if (startedFrom) {
      // Loading a version starts a new draft on top of the version in use now.
      setBase(current.version);
      setFromBuiltIn(startedFrom.builtIn);
    }
    if (note !== undefined) setNotes(note);
    setMessage(null);
    setPreviewOpen(false);
  };
  const startEditing = (body: string, startedFrom: { builtIn: boolean }, note: string) => {
    edit(body, startedFrom, note);
    setEditing(true);
  };
  const stopEditing = () => {
    edit(current.body, current, '');
    setConfirmCancel(false);
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      const { data: saved } = await save({ variables: { body: draft, notes, baseVersion: base, fromBuiltIn } });
      setDraft(saved.saveAiGuide.body);
      setBase(saved.saveAiGuide.version);
      setFromBuiltIn(false);
      setNotes('');
      setPreviewOpen(false);
      setEditing(false);
      setMessage({ ok: true, text: `Saved as version ${saved.saveAiGuide.version}. AI assistants get it the next time they open the guide.` });
    } catch (e) {
      // Someone else saved meanwhile: show what's in use now, keep this draft in the editor.
      refetch();
      refetchVersions();
      setMessage({ ok: false, text: (e as Error).message });
    }
  };

  const showPreview = () => {
    setPreviewOpen(true);
    runPreview({ variables: { body: draft } });
  };

  const loadLabel = (v: Guide) => (v.builtIn ? 'the built-in guide' : `version ${v.version}`);

  return (
    <div className={c.root} data-testid="ai-guide-editor">
      <span className={c.meta} data-testid="ai-guide-version">
        In use: version {current.version}{current.builtIn ? ' · built into Genesis' : `${current.createdBy ? ` · ${current.createdBy}` : ''}${current.createdAt ? ` · ${when(current.createdAt)}` : ''}`}
      </span>
      {current.builtInUpdated && builtIn && (
        <div className={c.notice} role="status" data-testid="ai-guide-builtin-updated">
          The guide built into Genesis has changed since this version was saved. Load it from History to see what’s new, and keep what you need from yours.
        </div>
      )}
      <div className={c.grid}>
        {editing ? (
          <div className={c.column}>
            <p className={g.muted}>
              This is the part marked “Guide text” above. Write it for an AI assistant, in Markdown: short instructions with the exact button names. Genesis adds everything else (the rules that always apply, the screens, the reader’s access, categories, templates and section code rules), so leave those out.
            </p>
            <label className={c.label} htmlFor="ai-guide-body">Guide text</label>
            <textarea id="ai-guide-body" ref={bodyRef} className={c.editor} value={draft} spellCheck={false} readOnly={saving} onChange={e => edit(e.target.value, null)} data-testid="ai-guide-body" />
            <span className={c.meta}>
              <span className={draft.length > MAX_GUIDE_CHARS ? c.over : undefined}>{draft.length.toLocaleString()}</span> of {MAX_GUIDE_CHARS.toLocaleString()} characters
            </span>
            <label className={c.label} htmlFor="ai-guide-notes">What changed</label>
            <input id="ai-guide-notes" className={c.input} placeholder="A short note for the history" value={notes} maxLength={500} readOnly={saving} onChange={(e) => { setNotes(e.target.value); setMessage(null); }} data-testid="ai-guide-notes" />
            {problems.length > 0 && <div className={g.error}>{problems.join(' ')}</div>}
            {message && <div className={message.ok ? g.ok : g.error} role={message.ok ? 'status' : 'alert'} data-testid="ai-guide-message">{message.text}</div>}
            <div className={c.actions}>
              <Button variant="primary" busy={saving} disabled={!changed || problems.length > 0} onClick={handleSave} data-testid="ai-guide-save">{saving ? 'Saving…' : 'Save as new version'}</Button>
              <Button icon="visibility" onClick={showPreview} disabled={saving || problems.length > 0} data-testid="ai-guide-preview-draft">Preview</Button>
              <Button variant="quiet" onClick={() => (changed ? setConfirmCancel(true) : stopEditing())} disabled={saving} data-testid="ai-guide-cancel">Cancel</Button>
            </div>
            {previewOpen && (
              <div className={c.column} data-testid="ai-guide-draft-preview">
                <span className={c.label}>What an AI would read{changed ? ' with your changes' : ''}</span>
                {previewError && <div className={g.error}>{previewError.message}</div>}
                {previewing && !previewData && <span className={c.meta}>Loading…</span>}
                {previewData && <GuideDocument text={previewData.aiGuideText} />}
              </div>
            )}
          </div>
        ) : (
          <div className={c.column}>
            <p className={g.muted}>
              Super admins write the part marked “Guide text” above: how to do common jobs in Genesis, in words an AI assistant can follow. Each save is a new version, so any earlier one can be brought back from History.
            </p>
            {message && <div className={message.ok ? g.ok : g.error} role={message.ok ? 'status' : 'alert'} data-testid="ai-guide-message">{message.text}</div>}
            <div className={c.actions}>
              <Button icon="edit" onClick={() => startEditing(current.body, current, '')} data-testid="ai-guide-start-edit">Edit guide</Button>
            </div>
          </div>
        )}
        <div className={c.column}>
          <span className={c.label}>History</span>
          {versionsError && <div className={g.error}>Couldn’t load the history: {versionsError.message}</div>}
          <ul className={c.versions} data-testid="ai-guide-versions">
            {versions.map(v => (
              <li key={v.builtIn ? 'built-in' : v.version} className={c.version} data-version={v.builtIn ? 'built-in' : v.version}>
                <strong>v{v.version}</strong>
                <span className={c.meta}>{v.builtIn ? 'built into Genesis' : [v.createdBy, when(v.createdAt), v.notes].filter(Boolean).join(' · ')}</span>
                <Button
                  size="sm"
                  disabled={saving}
                  onClick={() => startEditing(v.body, v, v.builtIn ? 'Back to the built-in guide' : `Back to v${v.version}`)}
                  title={`Open ${loadLabel(v)} in the editor. Nothing changes until you save.`}
                  aria-label={`Load ${loadLabel(v)} into the editor`}
                >
                  Load
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {confirmCancel && (
        <ConfirmDialog
          danger
          title="Discard your changes?"
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          onConfirm={stopEditing}
          onClose={() => setConfirmCancel(false)}
          testId="ai-guide-discard"
        >
          <p>Your changes to the guide aren’t saved. The guide stays as it is now.</p>
        </ConfirmDialog>
      )}
      {confirmLeave && (
        <ConfirmDialog
          danger
          title="Leave without saving?"
          confirmLabel="Leave"
          cancelLabel="Stay"
          onConfirm={leaveAnyway}
          onClose={() => { blockedRef.current = null; setConfirmLeave(false); }}
          testId="ai-guide-leave"
        >
          <p>Your changes to the AI guide aren’t saved.</p>
        </ConfirmDialog>
      )}
    </div>
  );
};

export default AiGuideEditor;
